import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { PageHeader } from "@/components/common/PageHeader";
import { DataTable } from "@/components/common/DataTable";
import { inboxService } from "@/services";
import {
  PieChart, Pie, Cell, ResponsiveContainer, Tooltip, BarChart, Bar, XAxis, YAxis,
} from "recharts";
import { Inbox, Clock, UserX, Loader2 } from "lucide-react";

const COLORS = ["#6366f1", "#06b6d4", "#f59e0b", "#10b981", "#ec4899", "#64748b"];

function Kpi({ label, value, icon: Icon, color }) {
  return (
    <div className="bg-card rounded-xl border border-border p-5 shadow-sm" data-testid={`sup-kpi-${label}`}>
      <div className="flex items-center justify-between">
        <p className="text-xs font-medium uppercase tracking-wider text-muted-foreground">{label}</p>
        <Icon className={`h-5 w-5 ${color}`} />
      </div>
      <p className="text-2xl font-bold text-foreground mt-3">{value ?? 0}</p>
    </div>
  );
}

function Card({ title, children, testid }) {
  return (
    <div className="bg-card rounded-xl border border-border p-5 shadow-sm" data-testid={testid}>
      <h3 className="font-semibold text-foreground mb-4">{title}</h3>
      {children}
    </div>
  );
}

export default function SupervisorPage() {
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => { inboxService.stats().then(setData).catch(() => {}).finally(() => setLoading(false)); }, []);

  if (loading) return <div className="flex justify-center py-20 text-muted-foreground"><Loader2 className="h-6 w-6 animate-spin" /></div>;

  const t = data?.totals || {};
  const byChannel = (data?.byChannel || []).filter((x) => x.value > 0);
  const byAgent = data?.byAgent || [];

  const agentCols = [
    { key: "agent", header: "Agent", render: (r) => <span className="font-medium text-foreground">{r.agent}</span> },
    { key: "value", header: "Open / Pending", render: (r) => r.value },
    {
      key: "_a", header: "",
      render: (r) => r.agentId && r.agentId !== "Unassigned" && (
        <div className="flex justify-end">
          <button onClick={() => navigate(`/admin/inbox?assignedAgentId=${r.agentId}`)} className="text-xs text-primary hover:underline" data-testid={`sup-view-agent-${r.agentId}`}>View queue</button>
        </div>
      ),
    },
  ];

  return (
    <div data-testid="supervisor-page">
      <PageHeader title="Supervisor View" subtitle="Cross-agent inbox overview, workload and channel mix" />

      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 mb-7">
        <Kpi label="Total" value={t.total} icon={Inbox} color="text-indigo-500" />
        <Kpi label="Open" value={t.open} icon={Inbox} color="text-sky-500" />
        <Kpi label="Pending" value={t.pending} icon={Clock} color="text-amber-500" />
        <Kpi label="Unassigned" value={t.unassigned} icon={UserX} color="text-rose-500" />
      </div>

      <div className="grid lg:grid-cols-2 gap-6 mb-6">
        <Card title="Conversations by Channel" testid="sup-by-channel">
          {byChannel.length === 0 ? <Empty /> : (
            <ResponsiveContainer width="100%" height={240}>
              <PieChart>
                <Pie data={byChannel} dataKey="value" nameKey="name" cx="50%" cy="50%" outerRadius={90} label>
                  {byChannel.map((_, i) => <Cell key={i} fill={COLORS[i % COLORS.length]} />)}
                </Pie>
                <Tooltip />
              </PieChart>
            </ResponsiveContainer>
          )}
        </Card>
        <Card title="Workload by Agent" testid="sup-by-agent-chart">
          {byAgent.length === 0 ? <Empty /> : (
            <ResponsiveContainer width="100%" height={240}>
              <BarChart data={byAgent}>
                <XAxis dataKey="agent" tick={{ fontSize: 10 }} interval={0} angle={-15} textAnchor="end" height={50} />
                <YAxis allowDecimals={false} tick={{ fontSize: 12 }} />
                <Tooltip />
                <Bar dataKey="value" radius={[6, 6, 0, 0]} fill="#6366f1" />
              </BarChart>
            </ResponsiveContainer>
          )}
        </Card>
      </div>

      <Card title="Agent Queues" testid="sup-agent-table">
        <DataTable columns={agentCols} rows={byAgent} loading={false} testid="sup-agents-table" empty="No active conversations" />
      </Card>
    </div>
  );
}

function Empty() {
  return <p className="text-sm text-muted-foreground py-10 text-center">No data yet</p>;
}
